import React, { useMemo, useState } from 'react';
import { ListIcon, SlidersHorizontalIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { CategoryKey } from './categoryData';
type CatalogView = 'grid' | 'list';
type SortOption = 'default' | 'name-asc' | 'name-desc';
type ElectronicsCatalogProps = {
  onCategorySelect: (categoryKey: CategoryKey) => void;
};
type ElectronicsProduct = {
  name: string;
  reference: string;
  image: string;
};
const EXPLORER_CATEGORIES: {
  key: CategoryKey;
  label: string;
  count: number;
}[] = [{
  key: 'alimentation',
  label: 'Alimentation',
  count: 47
}, {
  key: 'boisson',
  label: 'Boisson',
  count: 25
}, {
  key: 'electronique',
  label: 'Électronique',
  count: 6
}, {
  key: 'maison-decoration',
  label: 'Maison et Décoration',
  count: 16
}];
const PRODUCTS: ElectronicsProduct[] = [{
  name: 'MacBook Air 13” 256 Go SSD 16 Go RAM – Puce M2',
  reference: 'Ordinateur portable · M2',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/a6132517-e2aa-4724-9415-37cd9cb82b8a.jpg'
}, {
  name: 'MacBook Air 13” 256 Go SSD 16 Go RAM – Puce M3',
  reference: 'Ordinateur portable · M3',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/91d42706-7615-4292-9f09-5ecc5c8f1131.jpg'
}, {
  name: 'PC portable Zenbook 15” Ryzen 7, 32 Go RAM, 1 To',
  reference: 'Ordinateur portable · Ryzen 7',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/bd86d2e6-806c-40e6-b045-d49683fccfeb.jpg'
}, {
  name: 'MacBook Pro 14” 512 Go SSD – Puce Pro',
  reference: 'Ordinateur portable · Pro',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/1f631d8e-e5a9-4419-8fe8-a1a09626a91b.jpg'
}, {
  name: 'MacBook Air 15” 512 Go SSD 16 Go RAM',
  reference: 'Ordinateur portable · Grand écran',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/a6132517-e2aa-4724-9415-37cd9cb82b8a.jpg'
}, {
  name: 'PC portable premium 14” Intel Core Ultra',
  reference: 'Ordinateur portable · Core Ultra',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/1f631d8e-e5a9-4419-8fe8-a1a09626a91b.jpg'
}];
export function ElectronicsCatalog({
  onCategorySelect
}: ElectronicsCatalogProps) {
  const [sort, setSort] = useState<SortOption>('default');
  const [view, setView] = useState<CatalogView>('grid');
  const products = useMemo(() => {
    if (sort === 'default') return PRODUCTS;
    return [...PRODUCTS].sort((first, second) => {
      const comparison = first.name.localeCompare(second.name, 'fr');
      return sort === 'name-asc' ? comparison : -comparison;
    });
  }, [sort]);
  return <section className="mt-8 overflow-hidden rounded-2xl bg-white shadow-[0_18px_45px_rgba(10,51,32,0.12)]" aria-labelledby="electronics-catalog-title" data-id="element-112" data-mp="2hjrl54shrrq2">
      <div className="relative overflow-hidden bg-[#f4fae8] px-6 py-12 sm:px-10 lg:px-14 lg:py-16" data-id="element-113" data-mp="2cmhp36alkht9">
        <div className="absolute -right-10 -top-16 h-56 w-56 rounded-full border-[26px] border-ardis-yellow/35" aria-hidden="true" data-id="element-114" data-mp="3t0rapsngfsge" />
        <div className="absolute bottom-0 right-[15%] h-24 w-24 rounded-full border-[18px] border-ardis-green/10" aria-hidden="true" data-id="element-115" data-mp="3vijmbz8rwkt3" />
        <div className="relative" data-id="element-116" data-mp="4dkvxgvjexex">
          <p className="text-sm text-gray-500" data-id="element-117" data-mp="4af804yna1ks">Accueil <span aria-hidden="true" data-id="element-118" data-mp="3mj670pxyx20g">›</span> <span className="font-medium text-ardis-green" data-id="element-119" data-mp="3mk7mr7pxicoi">Électronique</span></p>
          <h3 id="electronics-catalog-title" className="mt-4 font-display text-4xl font-bold tracking-tight text-ardis-green sm:text-5xl" data-id="element-120" data-mp="3qkrkaceezib6">Électronique</h3>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-gray-600 sm:text-base" data-id="element-121" data-mp="n2zwla7b9kis">
            Découvrez notre sélection d’ordinateurs portables et d’équipements électroniques disponibles sur devis.
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-[230px_minmax(0,1fr)]" data-id="element-122" data-mp="dc57ly2ok6tn">
        <aside className="border-b border-gray-100 bg-gray-50/70 p-6 lg:border-b-0 lg:border-r lg:p-8" aria-label="Explorer les catégories" data-id="element-123" data-mp="112it15qrf7f9">
          <h4 className="font-display text-xl font-bold text-ardis-green" data-id="element-124" data-mp="15vbvto2o45op">Explorer</h4>
          <ul className="mt-5 space-y-1 border-t border-gray-200 pt-4" data-id="element-125" data-mp="3ebmoj8a96k9c">
            {EXPLORER_CATEGORIES.map(category => {
            const isCurrent = category.key === 'electronique';
            return <li key={category.key} data-id="element-126" data-mp="34dp5r0b5xfo4">
                  <button type="button" onClick={() => onCategorySelect(category.key)} aria-current={isCurrent ? 'page' : undefined} className={`flex w-full items-center justify-between rounded-md px-2 py-2 text-left text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ardis-blue focus:ring-offset-2 ${isCurrent ? 'text-ardis-green' : 'text-gray-700 hover:bg-white hover:text-ardis-green'}`} data-id="element-127" data-mp="34eh067t0x7ct">
                    <span className="flex items-center gap-2" data-id="element-128" data-mp="3ja6bnc0l223a">
                      <span className="text-ardis-yellow" aria-hidden="true" data-id="element-129" data-mp="3j7tydc5rsfwk">›</span>
                      {category.label}
                    </span>
                    <span className={isCurrent ? 'text-ardis-yellow' : 'text-gray-400'} data-id="element-130" data-mp="evgbrlw4iqoi">({category.count})</span>
                  </button>
                </li>;
          })}
          </ul>
        </aside>

        <div className="min-w-0 p-6 sm:p-8" data-id="element-131" data-mp="22grgxc9nu2x2">
          <div className="flex flex-col gap-4 border-b border-gray-100 pb-5 sm:flex-row sm:items-center sm:justify-between" data-id="element-132" data-mp="22ht1q8gha3rz">
            <p className="text-sm font-medium text-gray-700" data-id="element-133" data-mp="2bi7shnc7ob2v"><span className="font-semibold text-ardis-green" data-id="element-134" data-mp="2bj0opudjni6r">{products.length}</span> produits affichés</p>
            <div className="flex flex-wrap items-center gap-2" aria-label="Options d’affichage et de tri" data-id="element-135" data-mp="2ggsspmrgqurb">
              <div className="flex rounded-md border border-gray-200 bg-white p-0.5" role="group" aria-label="Mode d’affichage" data-id="element-136" data-mp="2lekkmgs3ygvi">
                <button type="button" aria-label="Afficher en grille" aria-pressed={view === 'grid'} onClick={() => setView('grid')} className={`rounded p-2 focus:outline-none focus:ring-2 focus:ring-ardis-blue ${view === 'grid' ? 'bg-ardis-green text-white' : 'text-gray-500 hover:text-ardis-green'}`} data-id="element-137" data-mp="2u8pgjem3mihw">
                  <div className="h-4 w-4" aria-hidden="true" data-id="element-138" data-mp="144nbtck3b9l4" />
                </button>
                <button type="button" aria-label="Afficher en liste" aria-pressed={view === 'list'} onClick={() => setView('list')} className={`rounded p-2 focus:outline-none focus:ring-2 focus:ring-ardis-blue ${view === 'list' ? 'bg-ardis-green text-white' : 'text-gray-500 hover:text-ardis-green'}`} data-id="element-139" data-mp="142awz89mpfca">
                  <ListIcon className="h-4 w-4" aria-hidden="true" data-id="element-140" data-mp="1mv4zh2q2fezm" />
                </button>
              </div>
              <label className="relative flex items-center" data-id="element-141" data-mp="1ittg3exsbpzv">
                <SlidersHorizontalIcon className="pointer-events-none absolute left-3 h-4 w-4 text-ardis-green" aria-hidden="true" data-id="element-142" data-mp="1it9vtrknwpio" />
                <span className="sr-only" data-id="element-143" data-mp="1ruhl5k57ag0m">Trier les produits</span>
                <select value={sort} onChange={event => setSort(event.target.value as SortOption)} className="h-10 appearance-none rounded-md border border-gray-200 bg-white py-2 pl-9 pr-9 text-sm font-medium text-gray-700 outline-none transition-colors focus:border-ardis-blue focus:ring-2 focus:ring-ardis-blue/20" data-id="element-144" data-mp="1rs55xeqtujr9">
                  <option value="default" data-id="element-145" data-mp="2e2nby11nl42y">Tri par défaut</option>
                  <option value="name-asc" data-id="element-146" data-mp="2e23v6nnqolqi">Nom, A à Z</option>
                  <option value="name-desc" data-id="element-147" data-mp="1zeirarj1iihj">Nom, Z à A</option>
                </select>
              </label>
            </div>
          </div>

          <motion.div layout className={view === 'grid' ? 'mt-8 grid gap-x-6 gap-y-10 sm:grid-cols-2 xl:grid-cols-3' : 'mt-6 space-y-4'} data-id="element-148" data-mp="24b0tbjmzk2q6">
            {products.map((product, index) => {
            const quoteUrl = `mailto:info@lepiandistribution.com?subject=${encodeURIComponent(`Demande de devis — ${product.name}`)}`;
            return <motion.article layout key={product.name} initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.28,
              delay: index * 0.04
            }} className={view === 'grid' ? 'flex flex-col' : 'flex flex-col gap-5 rounded-xl border border-gray-100 p-4 sm:flex-row sm:items-center'} data-id="element-149" data-mp="1ldzmdt9vfsjl" data-mpi={index}>
                  <div className={view === 'grid' ? 'aspect-square overflow-hidden bg-white' : 'h-36 w-full flex-shrink-0 overflow-hidden bg-white sm:w-44'} data-id="element-150" data-mp="1yjaoa8ngb7wy">
                    <img src={product.image} alt={product.name} className="h-full w-full object-contain" data-id="element-151" data-mp="1tqhkbmzt43lx" />
                  </div>
                  <div className={view === 'grid' ? 'flex flex-1 flex-col pt-4' : 'flex flex-1 flex-col'} data-id="element-152" data-mp="1jv64f1cnwqcl">
                    <p className="text-xs font-medium text-ardis-green/70" data-id="element-153" data-mp="1fst7n2fw82ry">{product.reference}</p>
                    <h5 className="mt-2 font-display text-sm font-semibold leading-relaxed text-gray-900" data-id="element-154" data-mp="1at6lg3i37c70">{product.name}</h5>
                    <a href={quoteUrl} className="mt-4 inline-flex w-fit items-center justify-center rounded-md bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2" data-id="element-155" data-mp="15wg4zf2deel2">
                      Demandez un devis
                    </a>
                  </div>
                </motion.article>;
          })}
          </motion.div>
        </div>
      </div>
    </section>;
}